import { Box, Divider, Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import {GetTheme, THEME} from '../../theme/customThemeProvider';
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import IconComponent from '../../components/icons/IconComponent';
import companyData from '../../assets/data/company.json';
import LinkedInIcon from '../../assets/icons/linkedin.svg';
import FacebookIcon from '../../assets/icons/facebook.svg';
import InstagramIcon from '../../assets/icons/instagram.svg';
import XIcon from '../../assets/icons/x.svg';
import YoutubeIcon from '../../assets/icons/youtube.svg';
import WhatsappIcon from '../../assets/icons/whatsapp.svg';
import EmailIcon from '../../assets/icons/gmail.svg';
import PlayStoreIcon from '../../assets/icons/play-store.svg';
import AppStoreIcon from '../../assets/icons/app-store.svg';

const companyList = [
    {label: "navigation.about_us", to: "/about-us"},
    {label: "footer.careers", to: "/careers"},
    {label: "navigation.case_studies", to: "/case-studies"},
    {label: "footer.blogs", to: "/blogs"},
    {label: "navigation.contact_us", to: "/contact-us"}
];

const servicesList = [
    {label: "services.home.list.ai.label", to: "/suite/ai"},
    {label: "services.home.list.games.label", to: "/suite/games"},
    {label: "services.home.list.web.label", to: "/suite/digital#web"},
    {label: "services.home.list.mobile.label", to: "/suite/digital#mobile"},
    {label: "services.home.list.cloud.label", to: "/suite/digital#cloud"},
    {label: "services.home.list.ux.label", to: "/digital-studio#uiux"},
    {label: "services.home.list.artworks.label", to: "/digital-studio#artworks"},
    {label: "services.home.list.animations.label", to: "/digital-studio#animations"},
    {label: "services.home.list.video.label", to: "/digital-studio#cinematic"}
];

const socialMediaList = [
    {icon: LinkedInIcon, alt: "Linkedin", to: companyData.contact.social_media.linkedin, sizeLg: 60, sizeSm: 50},
    {icon: FacebookIcon, alt: "Facebook", to: companyData.contact.social_media.facebook, sizeLg: 60, sizeSm: 50},
    {icon: InstagramIcon, alt: "Instagram", to: companyData.contact.social_media.instagram, sizeLg: 60, sizeSm: 50},
    {icon: XIcon, alt: "Twitter", to: companyData.contact.social_media.twitter, sizeLg: 65, sizeSm: 55},
    {icon: YoutubeIcon, alt: "Youtube", to: companyData.contact.social_media.youtube, sizeLg: 60, sizeSm: 50},
    {icon: WhatsappIcon, alt: "Whatsapp", to: companyData.contact.whatsapp_link, sizeLg: 57.5, sizeSm: 50},
    {icon: EmailIcon, alt: "Email", to: `mailto:${companyData.contact.email}`, sizeLg: 60, sizeSm: 50},
    {icon: AppStoreIcon, alt: "Email", to: companyData.contact.social_media.appStore, sizeLg: 45, sizeSm: 37.5},
    {icon: PlayStoreIcon, alt: "playStore", to: companyData.contact.social_media.playStore, sizeLg: 45, sizeSm: 37.5}
]

const FooterLayout = () => {
    const theme = useTheme();
    const {themeMode} = GetTheme();

    const {t} = useTranslation();

    return (
        <>
            <Box 
                width='100%'
                sx={{
                    backgroundColor: themeMode === THEME.DARK ? theme.palette.background.paper : theme.palette.text.primary
                }}
                pt={{xs: 5, md: 10}}
                pb={5}
            >
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    px='5%'
                    sx={{
                        overflow: 'hidden'
                    }}
                >
                    <Typography
                        fontWeight={900}
                        fontSize="clamp(1rem, 17.5vw, 16.5rem)"
                        textAlign='center'
                        lineHeight={1.25}
                        color={themeMode === THEME.DARK ? theme.palette.background.paper : theme.palette.text.primary}
                        sx={{
                            textShadow: `
                                -1px -1px 0 ${themeMode === THEME.DARK ? "#262626" : theme.palette.text.primary},
                                1px -1px 0 ${theme.palette.primary.contrastText},
                                -1px 1px 0 ${theme.palette.primary.contrastText},
                                1px 1px 0 ${theme.palette.primary.contrastText}
                            `,
                        }}
                    >
                        {t(`company`)}
                    </Typography>

                    <Typography
                        fontWeight={500}
                        fontSize={{xs: 12, md: 16}}
                        textAlign='center'
                        lineHeight={1.5}
                        color={theme.palette.text.secondary}
                        sx={{
                            mt: {xs: 0, md: -5}
                        }}
                    >
                        {t(`footer.description`)}
                    </Typography>

                    <Grid
                        direction='row'
                        justifyContent='start'
                        alignItems='start'
                        container
                        pt={{xs: 5, md: 10}}
                        width='100%'
                        pb={5}
                    >
                        <Grid size={4}>
                            <FooterPCOptions title={t('footer.company')} list={companyList}/>
                        </Grid>

                        <Grid size={4}>
                            <FooterPCOptions title={t('footer.services')} list={servicesList}/>
                        </Grid>

                        <Grid size={{xs: 4, lg: 3}}>
                            <FooterPCLocation />
                        </Grid>

                        <Grid size={{xs: 12, lg: 1}}>
                            <FooterPCSocialMedia />
                        </Grid>
                    </Grid>

                    <Divider 
                        style={{
                            width: '100%',
                            backgroundColor: 'white'
                        }}
                    />

                    <Stack
                        direction={{xs: 'column-reverse', md: 'row'}}
                        justifyContent='space-between'
                        alignItems='center'
                        width='100%'
                    >
                        <Typography
                            fontWeight={500}
                            fontSize={12}
                            textAlign='left'
                            lineHeight={1.5}
                            color={theme.palette.text.secondary}
                            pt={5}
                        >
                            {`© ${new Date().getFullYear()} ${t('company')}. ${t(`footer.copyrights`)}`}
                        </Typography>

                        <Stack
                            direction='row'
                            justifyContent='end'
                            alignItems='center'
                            spacing={5}
                            pt={5}
                        >
                            <Link 
                                to="/privacy-policy"
                                style={{
                                    textDecoration: 'none'
                                }}
                            >
                                <Typography
                                    fontWeight={500}
                                    fontSize={12}
                                    textAlign='left'
                                    lineHeight={1.5}
                                    color={theme.palette.text.secondary}
                                    sx={{
                                        '&: hover': {
                                            transform: 'scale(1.1)',
                                            color: theme.palette.primary.contrastText
                                        }
                                    }}
                                >
                                    {t(`footer.privacy_policy`)}
                                </Typography>
                            </Link>

                            <Link 
                                to="/terms-and-conditions"
                                style={{
                                    textDecoration: 'none'
                                }}
                            >
                                <Typography
                                    fontWeight={500}
                                    fontSize={12}
                                    textAlign='left'
                                    lineHeight={1.5}
                                    color={theme.palette.text.secondary}
                                    sx={{
                                        '&: hover': {
                                            transform: 'scale(1.1)',
                                            color: theme.palette.primary.contrastText
                                        }
                                    }}
                                >
                                    {t(`footer.terms_and_condition`)}
                                </Typography>
                            </Link>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
        </>
    );
}

export default FooterLayout;

const FooterPCOptions = ({
    title,
    list
}) => {
    const theme = useTheme();
    const {t} = useTranslation();

    return (
        <>
            <Stack
                direction='column'
                justifyContent='start'
                alignItems='start'
            >
                <Typography
                    fontWeight={600}
                    fontSize={{xs: 12, md: 16}}
                    textAlign='left'
                    lineHeight={1.5}
                    color='white'
                >
                    {title}
                </Typography>

                <Divider 
                    sx={{
                        width: '50%', 
                        height: 1,
                        backgroundColor: 'white',
                        my: 2
                    }}
                />

                {
                    list.map((item, index) => {
                        return <Link
                            key={index}
                            to={item.to}
                            style={{
                                textDecoration: 'none'
                            }}
                        >
                            <Typography
                                fontWeight={400}
                                fontSize={{xs: 12, md: 14}}
                                textAlign='left'
                                lineHeight={1.5}
                                color='white'
                                py={1}
                                sx={{
                                    '&: hover': {
                                        transform: 'scale(1.1)'
                                    }
                                }}
                            >
                                {t(`${item.label}`)}
                            </Typography>
                        </Link>
                    })
                }
            </Stack>
        </>
    );
}

const FooterPCLocation = () => {
    const theme = useTheme();
    const {t} = useTranslation();

    return (
        <>
            <Stack
                direction='column'
                justifyContent='start'
                alignItems='start'
                pl={{xs: 5, md: 0}}
            >
                <Box
                    sx={{
                        width: {xs: 45, md: 75}
                    }}
                >
                    <img 
                        src={t('footer.location.sri_lanka.flag')}
                        alt="country" 
                        width='100%' 
                    />
                </Box>

                <Typography
                    fontWeight={700}
                    fontSize={{xs: 12, md: 14}}
                    textAlign='left'
                    lineHeight={1.5}
                    color={theme.palette.primary.contrastText}
                    py={1}
                >
                    {`${t('company')} ${t('footer.location.sri_lanka.country')}`}
                </Typography>

                <Typography
                    fontWeight={400}
                    fontSize={{xs: 12, md: 14}}
                    textAlign='left'
                    lineHeight={1.5}
                    color='white'
                >
                    {t('footer.location.sri_lanka.add_line_1')}
                </Typography>

                <Typography
                    fontWeight={400}
                    fontSize={{xs: 12, md: 14}}
                    textAlign='left'
                    lineHeight={1.5}
                    color='white'
                >
                    {t('footer.location.sri_lanka.add_line_2')}
                </Typography>

                <Typography
                    fontWeight={400}
                    fontSize={{xs: 12, md: 14}}
                    textAlign='left'
                    lineHeight={1.5}
                    color='white'
                >
                    {t('footer.location.sri_lanka.add_line_3')}
                </Typography>

                <Typography
                    fontWeight={400}
                    fontSize={{xs: 12, md: 14}}
                    textAlign='left'
                    lineHeight={1.5}
                    color='white'
                >
                    {t('footer.location.sri_lanka.city')}
                </Typography>

                <Typography
                    fontWeight={400}
                    fontSize={{xs: 12, md: 14}}
                    textAlign='left'
                    lineHeight={1.5}
                    color='white'
                >
                    {t('footer.location.sri_lanka.country')}
                </Typography>
            </Stack>
        </>
    );
}

const FooterPCSocialMedia = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <>
            <Grid
                direction='row'
                justifyContent={{xs: 'center', lg: 'end'}}
                alignItems='center'
                container
                pt={{xs: 3, lg: 0}}
            >
                {
                    socialMediaList.map((item, index) => {
                        return <Grid 
                            key={index} 
                            size={{xs: 2, lg: 6}}
                            display='flex'
                            direction='row'
                            justifyContent='center'
                            alignItems='center'
                            sx={{
                                '&: hover': {
                                    transform: 'scale(0.9)'
                                }
                            }}
                        >
                            <Link 
                                to={item.to}
                                target="_black"
                                style={{
                                    textDecoration: 'none'
                                }}
                            >
                                <IconComponent 
                                    icon={item.icon}
                                    alt={item.alt}
                                    size={isMobile ? item.sizeSm : item.sizeLg}
                                />
                            </Link>
                        </Grid>
                    })
                }
            </Grid>
        </>
    );
}